import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth.js';
import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '../../utils/formatters.js';
import { downloadPurchaseOrderPdf, previewPurchaseOrderPdf } from '../../utils/pdf.js';

import { PurchaseOrderSendModal } from './PurchaseOrderSendModal.jsx';
import { ReceivePurchaseOrderModal } from './ReceivePurchaseOrderModal.jsx';

export function PurchaseOrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // UX mirror only - the inventory-service enforces the admin requirement.
  const { roles } = useAuth();
  const canDecideApprovals = roles.includes('admin');

  const po = useQuery({
    queryKey: ['inventory', 'purchase-orders', id],
    queryFn: () => inventoryApi.getPurchaseOrder(id).then((r) => r.items),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers().then((r) => r.items || []),
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items || []),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
  });

  // The list key is a prefix of the detail key, so this covers both - plus the insights strip.
  const invalidatePO = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'purchase-orders'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'po-insights'] });
  };
  // 403s already surface via the global forbidden handler - avoid a double toast.
  const toastError = (err) => {
    if (err?.status !== 403) showToast(err.message);
  };

  const confirm = useMutation({
    mutationFn: () => inventoryApi.confirmPO(id),
    onSuccess: (updated) => {
      invalidatePO();
      // Above the approval threshold confirm parks the PO instead of confirming.
      if (updated?.status === 'PENDING_APPROVAL') {
        showToast(t('inv.po.approval.submitted'), 'success');
      }
    },
    onError: toastError,
  });
  const cancel = useMutation({
    mutationFn: () => inventoryApi.cancelPO(id),
    onSuccess: invalidatePO,
    onError: toastError,
  });
  const approve = useMutation({
    mutationFn: () => inventoryApi.approvePO(id),
    onSuccess: invalidatePO,
    onError: toastError,
  });
  const reject = useMutation({
    mutationFn: () => inventoryApi.rejectPO(id),
    onSuccess: () => {
      invalidatePO();
      setRejectOpen(false);
    },
    onError: (err) => {
      setRejectOpen(false);
      toastError(err);
    },
  });

  if (po.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (po.error || !po.data) {
    return (
      <section className="page">
        <DetailHeader backTo="/inventory/purchase-orders" title={t('inv.po.title')} />
        <div className="alert danger">{po.error?.message || 'Not found'}</div>
      </section>
    );
  }

  const p = po.data;
  const supplier = (suppliers.data || []).find((s) => s.id === p.supplierId);
  const warehouse = (warehouses.data || []).find((w) => w.id === p.warehouseId);

  const actions = [
    <Button key="preview" variant="ghost" onClick={() => previewPurchaseOrderPdf({ po: p, supplier, settings: settings.data, t })}>
      {t('pdf.preview')}
    </Button>,
    <Button key="pdf" variant="ghost" onClick={() => downloadPurchaseOrderPdf({ po: p, supplier, settings: settings.data, t })}>
      {t('invoice.print.downloadPdf')}
    </Button>,
  ];
  // Suppliers only get confirmed orders - drafts and unapproved POs stay internal.
  if (p.status === 'CONFIRMED' || p.status === 'PARTIAL' || p.status === 'RECEIVED') {
    actions.push(
      <Button key="email" variant="ghost" onClick={() => setSendOpen(true)}>
        {t('inv.po.action.email')}
      </Button>,
    );
  }
  if (p.status === 'DRAFT') {
    actions.push(
      <Button key="confirm" variant="primary" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
        {t('inv.po.action.confirm')}
      </Button>,
      <Button key="cancel" variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
        {t('inv.po.action.cancel')}
      </Button>,
    );
  }
  if (p.status === 'PENDING_APPROVAL' && canDecideApprovals) {
    actions.push(
      <Button key="approve" variant="primary" onClick={() => approve.mutate()} disabled={approve.isPending}>
        {t('inv.po.action.approve')}
      </Button>,
      <Button key="reject" variant="danger" onClick={() => setRejectOpen(true)} disabled={reject.isPending}>
        {t('inv.po.action.reject')}
      </Button>,
    );
  }
  if (p.status === 'CONFIRMED' || p.status === 'PARTIAL') {
    actions.push(
      <Button key="receive" variant="primary" onClick={() => setReceiveTarget(p)}>
        {t('inv.po.action.receive')}
      </Button>,
    );
  }

  return (
    <section className="page">
      <DetailHeader
        backTo="/inventory/purchase-orders"
        title={p.poNumber}
        status={p.status}
        actions={actions}
        metrics={[
          { label: t('detail.metric.total'), value: formatCurrency(p.totalAmount, p.currency) },
          { label: t('detail.metric.date'), value: formatDate(p.orderDate) },
          ...(p.expectedDate
            ? [{ label: t('detail.metric.expected'), value: formatDate(p.expectedDate) }]
            : []),
        ]}
      />

      <Card title={t('detail.header')}>
        <FieldGrid items={[
          { label: t('inv.po.modal.supplier'),  value: supplier ? `${supplier.code} - ${supplier.name}` : p.supplierId },
          { label: t('inv.po.modal.warehouse'), value: warehouse ? `${warehouse.code} - ${warehouse.name}` : p.warehouseId },
          { label: t('inv.po.col.date'),        value: formatDate(p.orderDate) },
          { label: t('inv.po.col.expected'),    value: formatDate(p.expectedDate) },
          { label: t('detail.receivedAt'),      value: formatDateTime(p.receivedAt) },
          { label: t('col.total'),              value: <strong className="mono">{formatCurrency(p.totalAmount, p.currency)}</strong> },
          { label: t('detail.notes'),           value: p.notes },
        ]} />
      </Card>

      {(p.requestedBy || p.approvedBy || p.approvalNote) && (
        <Card title={t('inv.po.approval.title')}>
          <FieldGrid items={[
            ...(p.requestedBy
              ? [{ label: t('inv.po.approval.requestedBy'), value: <span className="mono">{String(p.requestedBy).slice(0, 8)}</span> }]
              : []),
            ...(p.approvedBy
              ? [{ label: t('inv.po.approval.decidedBy'), value: <span className="mono">{String(p.approvedBy).slice(0, 8)}</span> }]
              : []),
            ...(p.approvalDecidedAt
              ? [{ label: t('inv.po.approval.decidedAt'), value: formatDateTime(p.approvalDecidedAt) }]
              : []),
            ...(p.approvalNote
              ? [{ label: t('inv.po.approval.note'), value: p.approvalNote }]
              : []),
          ]} />
        </Card>
      )}

      <Card title={t('detail.lines')}>
        <Table
          rows={p.lines || []}
          getRowKey={(row) => row.id}
          columns={[
            { key: 'product', label: t('detail.lineProduct'), render: (r) => r.sku ? `${r.sku} - ${r.productName}` : r.productId.slice(0, 8) },
            { key: 'quantity', label: t('detail.lineQty'), align: 'right', render: (r) => formatNumber(r.quantity) },
            { key: 'receivedQuantity', label: t('detail.lineReceived'), align: 'right', render: (r) => formatNumber(r.receivedQuantity) },
            { key: 'unitCost', label: t('detail.lineUnitCost'), align: 'right', render: (r) => formatCurrency(r.unitCost, p.currency) },
            { key: 'lineAmount', label: t('detail.lineTotal'), align: 'right', render: (r) => formatCurrency(r.lineAmount, p.currency) },
          ]}
        />
      </Card>

      <ReceivePurchaseOrderModal po={receiveTarget} onClose={() => setReceiveTarget(null)} />
      <PurchaseOrderSendModal po={sendOpen ? p : null} onClose={() => setSendOpen(false)} />
      <ConfirmDialog
        open={rejectOpen}
        title={t('inv.po.action.reject')}
        message={t('inv.po.approval.confirmReject')}
        confirmLabel={t('inv.po.action.reject')}
        variant="danger"
        isPending={reject.isPending}
        onConfirm={() => reject.mutate()}
        onClose={() => setRejectOpen(false)}
      />
    </section>
  );
}
